import * as React from "react";
import axios from "axios";

import Loading from "./Loading";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Title from "../components/Layout/Title";
import Snackbar from "@mui/material/Snackbar";
import MenuItem from "@mui/material/MenuItem";
import MuiAlert from "@mui/material/Alert";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";

import { Formik } from "formik";
import * as yup from "yup";

import { useParams } from "react-router-dom";
import { Button } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";

export default function Org() {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const { org_id } = useParams();
  const [btnLoading, setBtnLoading] = React.useState(false);

  //alert states:
  const [open, setOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [alertColour, setAlertColour] = React.useState();

  //list of orgs to check if name is unique
  const [allOrgs, setAllOrgs] = React.useState([]);

  const [pageData, setPageData] = React.useState({
    org: {},
    users: {},
    full_accounts: 0,
  });

  //optomisting rendering alert

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const [formUnlocked, setFormUnlocked] = React.useState(false);

  function toggleForm() {
    setFormUnlocked(!formUnlocked);
  }

  React.useEffect(() => {
    setLoading(true);

    if (org_id) {
      const fetchData = async () => {
        const orgData = await axios.get(`/orgs/${org_id}`);
        const orgs = await axios.get("/orgs");

        setPageData(orgData.data);

        const orgArr = orgs.data.orgs.filter(
          (org) => orgData.data.org.client !== org.client
        );

        const orgNames = orgArr.map((org) => org.client.toLowerCase());

        setAllOrgs(orgNames);
      };
      fetchData()
        .then(() => {
          setLoading(false);
        })
        .catch((e) => {
          setErr(e.response.data);

          setLoading(false);
        });
    }
  }, []);

  // Form validation:
  const userSchema = yup.object().shape({
    subscriptionType: yup.string().required("Required"),
    accountType: yup.string().required("Required"),
    tolerance: yup.number().min(0).max(100).required("Required"),
    remaining: yup.number().min(0).required("Required"),
    enddate: yup.date("properdate please"),
    type: yup.string().required("Required"),
    organization: yup
      .string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .matches(
        /^[a-zA-Z0-9\s\-]+$/,
        "Organisation name must only include letters and numbers"
      )
      .required("Required")
      .transform((value) => value.toLowerCase())
      .notOneOf(
        allOrgs.map((org) => org),
        "Organisation already exists"
      ),
  });

  //coverts date value to yyyy/mm/dd before post
  function dateFormat(date) {
    const newDate =
      "" +
      dayjs(date).year() +
      "-" +
      ("0" + (dayjs(date).month() + 1)).substring(
        ("0" + (dayjs(date).month() + 1)).length - 2
      ) +
      "-" +
      ("0" + dayjs(date).date()).substring(
        ("0" + dayjs(date).date()).length - 2
      );
    return newDate;
  }

  // initial values to populate Formik form with
  const initialValues = {
    organization: pageData.org.client,
    subscriptionType: pageData.org.level,
    keyOwner: !pageData.users[0]
      ? "none"
      : pageData.users[0].firstName + " " + pageData.users[0].lastName,
    keyValue: pageData.org.keynum,
    accountType: pageData.org.account_type,
    endDate: pageData.org.expiry,
    subscriptions: pageData.org.subscriptions,
    tolerance: pageData.org.tolerance != null ? pageData.org.tolerance : 0,
    remaining: pageData.org.remaining,
    used: pageData.org.used,
    type: pageData.org.keyType,
  };

  const handleSubmit = (values) => {
    setBtnLoading(true);

    values.endDate = dateFormat(values.endDate);

    axios
      .patch(`/orgs/${org_id}`, {
        id: pageData.org.id,
        values: values,
      })
      .then((res) => {
        setBtnLoading(false);

        toggleForm();
        setAlertColour("success");
        setMessage(res.data);
        setOpen(true);
      })
      .catch((e) => {
        setBtnLoading(false);
        setAlertColour("error");
        setMessage(e.response.data);
        setOpen(true);
      });
  };

  return (
    <div>
      {err ? (
        <p>{err}</p>
      ) : (
        (loading && <Loading />) || (
          <Grid id="i" container spacing={3}>
            <Grid item xs={12} md={4} lg={3}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: "auto",
                }}
              >
                <Title>Organization</Title>
                <Button onClick={toggleForm}>
                  {formUnlocked ? "Lock" : "Edit"}
                </Button>

                <Snackbar
                  open={open}
                  autoHideDuration={6000}
                  onClose={handleAlertClose}
                >
                  {
                    <Alert
                      onClose={handleAlertClose}
                      severity={alertColour}
                      sx={{ width: "100%" }}
                    >
                      {message}
                    </Alert>
                  }
                </Snackbar>

                <Formik
                  onSubmit={handleSubmit}
                  initialValues={initialValues}
                  validationSchema={userSchema}
                  validateOnChange={false}
                  validateOnBlur={true}
                >
                  {({
                    values,
                    errors,
                    touched,
                    resetForm,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    setFieldValue,
                    setFieldTouched,
                    isValidating,
                    dirty,
                  }) => (
                    <form onSubmit={handleSubmit} onReset={resetForm}>
                      <Box
                        id="editForm"
                        sx={{
                          mt: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <TextField
                          margin="normal"
                          fullWidth
                          id="organization"
                          label="Organization name"
                          name="organization"
                          variant="standard"
                          value={values.organization}
                          disabled={!formUnlocked}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            !!touched.organization && !!errors.organization
                          }
                          helperText={
                            touched.organization && errors.organization
                          }
                        />

                        <TextField
                          select
                          margin="normal"
                          fullWidth
                          variant="standard"
                          name="subscriptionType"
                          value={values.subscriptionType}
                          disabled={!formUnlocked}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            !!touched.subscriptionType &&
                            !!errors.subscriptionType
                          }
                          helperText={
                            touched.subscriptionType && errors.subscriptionType
                          }
                          label="Subscription type"
                        >
                          <MenuItem key={"premium"} value="premium">
                            premium
                          </MenuItem>
                          <MenuItem key={"standard"} value="standard">
                            standard
                          </MenuItem>
                        </TextField>
                        <TextField
                          margin="normal"
                          fullWidth
                          id="keyowner"
                          label="Key owner"
                          name="KeyOwner"
                          variant="standard"
                          value={values.keyOwner}
                          disabled={true}
                          error={!!touched.KeyOwner && !!errors.KeyOwner}
                          helperText={touched.KeyOwner && errors.KeyOwner}
                        />
                        <TextField
                          select
                          fullWidth
                          margin="normal"
                          variant="standard"
                          name="accountType"
                          value={values.accountType}
                          disabled={!formUnlocked}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          label="Default Account Type"
                          error={!!touched.accountType && !!errors.accountType}
                          helperText={touched.accountType && errors.accountType}
                        >
                          <MenuItem key={"plus"} value="plus">
                            plus
                          </MenuItem>
                          <MenuItem key={"standard"} value="standard">
                            standard
                          </MenuItem>
                          <MenuItem key={"trial"} value="trial">
                            trial
                          </MenuItem>
                          <MenuItem key={"free"} value="free">
                            free
                          </MenuItem>
                        </TextField>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            label="expiry date"
                            inputFormat="DD/MM/YYYY"
                            variant="standard"
                            name="endDate"
                            value={values.endDate}
                            disabled={!formUnlocked}
                            error={!!touched.endDate && !!errors.endDate}
                            helperText={touched.endDate && errors.endDate}
                            renderInput={(params) => {
                              return (
                                <TextField
                                  {...params}
                                  fullWidth
                                  margin="normal"
                                  variant="standard"
                                  onKeyDown={(e) => e.preventDefault()}
                                />
                              );
                            }}
                            onChange={(newValue) => {
                              setFieldValue("endDate", newValue.$d);
                            }}
                          />
                        </LocalizationProvider>

                        <TextField
                          margin="normal"
                          fullWidth
                          value={values.subscriptions}
                          name="subscriptions"
                          variant="standard"
                          disabled={true}
                          label="Subscriptions"
                        ></TextField>
                        <TextField
                          margin="normal"
                          fullWidth
                          id="keyvalue"
                          label="Key value"
                          name="keyValue"
                          variant="standard"
                          value={values.keyValue}
                          disabled={true}
                          error={!!touched.Keyvalue && !!errors.Keyvalue}
                          helperText={touched.Keyvalue && errors.Keyvalue}
                        />
                        <TextField
                          type="number"
                          InputProps={{ inputProps: { min: 0 } }}
                          margin="normal"
                          fullWidth
                          value={values.tolerance}
                          variant="standard"
                          disabled={!formUnlocked}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          label="Risk tolerance"
                          name="tolerance"
                          error={!!touched.tolerance && !!errors.tolerance}
                          helperText={touched.tolerance && errors.tolerance}
                        ></TextField>
                        <TextField
                          type="number"
                          InputProps={{ inputProps: { min: 0 } }}
                          margin="normal"
                          fullWidth
                          value={values.remaining}
                          variant="standard"
                          name="remaining"
                          disabled={!formUnlocked}
                          label="Remaining"
                          error={!!touched.remaining && !!errors.remaining}
                          helperText={touched.remaining && errors.remaining}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        ></TextField>
                        <TextField
                          type="number"
                          margin="normal"
                          fullWidth
                          label="Used"
                          name="used"
                          variant="standard"
                          value={values.used}
                          disabled={true}
                          // error={!!touched.used && !!errors.used}
                          // helperText={touched.used && errors.used}
                          // onChange={handleChange}
                          // onBlur={handleBlur}
                        />
                        <TextField
                          select
                          margin="normal"
                          fullWidth
                          name="type"
                          value={values.type}
                          variant="standard"
                          disabled={!formUnlocked}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!touched.type && !!errors.type}
                          helperText={touched.type && errors.type}
                          label="Key type"
                        >
                          <MenuItem key={"org"} value="org">
                            org
                          </MenuItem>
                          <MenuItem key={"cons"} value="cons">
                            cons
                          </MenuItem>
                        </TextField>
                        <LoadingButton
                          loading={btnLoading || isValidating}
                          type="submit"
                          variant="contained"
                          sx={{ mt: 3, mb: 2 }}
                          disabled={!formUnlocked || !dirty}
                        >
                          Update
                        </LoadingButton>
                        <Button
                          type="reset"
                          variant="contained"
                          color="secondary"
                        >
                          Reset
                        </Button>
                      </Box>
                    </form>
                  )}
                </Formik>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8} lg={9}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: "auto",
                  overflow: "auto",
                }}
              >
                <Title>Users</Title>
                {!pageData.users[0] ? (
                  <p>This org has no users</p>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Org</TableCell>
                        <TableCell>Account type</TableCell>
                        <TableCell>Account status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pageData.users.map((user) => (
                        <TableRow key={user.keyId}>
                          <TableCell>{user.user}</TableCell>
                          <TableCell>
                            {user.firstName + " " + user.lastName}
                          </TableCell>
                          <TableCell>{user.company}</TableCell>
                          <TableCell>{user.config}</TableCell>
                          <TableCell>{user.stateName}</TableCell>
                          <TableCell>{"-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </Grid>
          </Grid>
        )
      )}
    </div>
  );
}
